import React from 'react';
import './App.css';
import TenantHeader from './TenantHeader';
import { useNavigate } from 'react-router-dom';
import { formatListingPricePerMonth, useTenantPortalProfile } from './TenantPortalContext';

function initialsFromName(name) {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

const rentStatusLabel = {
  paid: 'Paid',
  pending: 'Pending',
  overdue: 'Overdue',
};

function formatLeaseRange(moveIn, moveOut) {
  if (!moveIn && !moveOut) {
    return 'Move-in and move-out dates are not set on your record yet.';
  }
  const opts = { day: 'numeric', month: 'short', year: 'numeric' };
  try {
    const a = moveIn ? new Date(moveIn).toLocaleDateString(undefined, opts) : '—';
    const b = moveOut ? new Date(moveOut).toLocaleDateString(undefined, opts) : '—';
    return `Lease: ${a} – ${b}`;
  } catch {
    return 'Lease dates on file.';
  }
}

function TenantDashboard() {
  const navigate = useNavigate();
  const { profile, property, session } = useTenantPortalProfile();

  const firstName = (profile.full_name || '').trim().split(/\s+/)[0] || 'Tenant';
  const room = (profile.room_label || '').trim();
  const homeTitle = [property?.name, room || null].filter(Boolean).join(' · ') || 'Your assignment';
  const addressLine = (property?.location || '').trim() || '—';
  const rentLabel =
    rentStatusLabel[profile.rent_status] || profile.rent_status || '—';
  const listingRent = formatListingPricePerMonth(property?.price_per_month);

  return (
    <div className="tenant-root">
      <TenantHeader />
      <header className="tenant-topbar">
        <div className="tenant-topbar-left">
          <div className="tenant-logo-mark">RO</div>
          <div className="tenant-topbar-text">
            <span className="tenant-topbar-title">ODUS</span>
            <span className="tenant-topbar-subtitle">Tenant portal</span>
          </div>
        </div>
        <div className="tenant-topbar-right">
          <button className="tenant-icon-btn" aria-label="Notifications">
            🔔
          </button>
          <div className="tenant-profile">
            <div className="tenant-profile-avatar">{initialsFromName(profile.full_name)}</div>
            <span className="tenant-profile-name">{firstName}</span>
          </div>
        </div>
      </header>

      <main className="tenant-layout">
        <section className="tenant-summary">
          <article className="tenant-card">
            <h2 className="tenant-card-label">Your home</h2>
            <p className="tenant-card-main">{homeTitle}</p>
            <p className="tenant-card-address">{addressLine}</p>
            <p className="tenant-card-sub">{formatLeaseRange(profile.move_in_date, profile.move_out_date)}</p>
            {listingRent ? (
              <p className="tenant-card-main" style={{ marginTop: 10 }}>
                Monthly rent (listing): {listingRent}
              </p>
            ) : (
              <p className="tenant-card-sub" style={{ marginTop: 10 }}>
                Monthly rent is not set on this property yet. Ask your landlord to add it under Properties.
              </p>
            )}
          </article>

          <article className="tenant-card">
            <h2 className="tenant-card-label">Rent status</h2>
            <p className="tenant-card-main">{rentLabel}</p>
            <p className="tenant-card-sub">
              This reflects your record on file. Payment tools can be connected here later.
            </p>
            <button
              type="button"
              className="btn-primary tenant-pay-btn"
              onClick={() => navigate('/tenant/rent')}
            >
              View rent & payments
            </button>
          </article>

          <article className="tenant-card">
            <h2 className="tenant-card-label">Your account</h2>
            <p className="tenant-card-main">{profile.email || session.email || '—'}</p>
            <p className="tenant-card-sub">
              Signed in as <strong>{profile.full_name || session.fullName || 'Tenant'}</strong>
            </p>
          </article>
        </section>

        <section className="tenant-main-grid">
          <section className="tenant-panel">
            <div className="tenant-panel-header">
              <div>
                <h3 className="tenant-panel-title">Maintenance</h3>
                <p className="tenant-panel-subtitle">
                  Requests for <strong>{homeTitle}</strong> will show here once linked to a work order system.
                </p>
              </div>
              <button
                type="button"
                className="btn-primary tenant-request-btn"
                onClick={() => navigate('/tenant/maintenance')}
              >
                + Submit request
              </button>
            </div>

            <ul className="tenant-requests">
              <li className="tenant-request-item">
                <div>
                  <p className="tenant-request-title">No tickets on file yet</p>
                  <p className="tenant-request-sub">
                    Use Submit request to log an issue for your unit.
                  </p>
                </div>
                <span className="tenant-status-badge tenant-status-badge--pending">New</span>
              </li>
            </ul>
          </section>

          <section className="tenant-panel">
            <h3 className="tenant-panel-title">Notifications</h3>
            <ul className="tenant-notifications">
              <li className="tenant-notification">
                <p className="tenant-notification-title">Personal portal</p>
                <p className="tenant-notification-sub">
                  You are viewing data for <strong>{profile.full_name || 'your account'}</strong> at{' '}
                  <strong>{homeTitle}</strong>.
                </p>
              </li>
              <li className="tenant-notification">
                <p className="tenant-notification-title">Contact</p>
                <p className="tenant-notification-sub">
                  We have <strong>{profile.phone?.trim() || 'no phone'}</strong> on file. Ask your landlord to
                  update your record if this is wrong.
                </p>
              </li>
            </ul>
          </section>

          <section className="tenant-panel tenant-panel--full">
            <div className="tenant-panel-header">
              <div>
                <h3 className="tenant-panel-title">Feedback</h3>
                <p className="tenant-panel-subtitle">
                  Notes you send are tied to your login ({profile.email || session.email}).
                </p>
              </div>
              <button
                type="button"
                className="btn-primary tenant-request-btn"
                onClick={() => navigate('/tenant/feedback')}
              >
                Open feedback form
              </button>
            </div>
          </section>
        </section>
      </main>
    </div>
  );
}

export default TenantDashboard;
